import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import api from "../../api/client";
import "./HomePage.css";

type DueReminder = {
  maid_id: number;
  maid_name: string;
  client_id?: number;
  client_name: string;
  contact_info: string;
  reminder_type: string;
  reminder_label?: string;
  due_date: string;
  arrival_date?: string | null;
  insurance_start_date?: string | null;
  completed: boolean;

  permit: number;
  insurance: number;
  employment_contract: number;
  profit: number;
  cost: number;
  total: number;
};

type FinancialForm = {
  permit: string;
  insurance: string;
  employment_contract: string;
  profit: string;
};

const HomePage: React.FC = () => {
  // ============================================================
  // REDUX
  // ============================================================

  const email = useSelector((state: RootState) => state.user.email);
  const token = useSelector((state: RootState) => state.user.token);
  const agencyName = useSelector((state: RootState) => state.user.agency_name);

  // ============================================================
  // STATE
  // ============================================================

  const [dueReminders, setDueReminders] = useState<DueReminder[]>([]);
  const [error, setError] = useState<string>("");

  const [completingReminder, setCompletingReminder] = useState<string | null>(
    null
  );

  const [expandedReminder, setExpandedReminder] = useState<string | null>(null);

  const [financialForms, setFinancialForms] = useState<
    Record<string, FinancialForm>
  >({});

  const [savingFinancials, setSavingFinancials] = useState<string | null>(null);

  const [financialMessages, setFinancialMessages] = useState<
    Record<string, string>
  >({});

  // ============================================================
  // UNIQUE REMINDER KEY
  // ============================================================

  const getReminderKey = (reminder: DueReminder) => {
    return `${reminder.maid_id}-${reminder.reminder_type}-${reminder.due_date}`;
  };

  // ============================================================
  // REMINDER LABEL
  // ============================================================

  const getReminderLabel = (reminder: DueReminder) => {
    if (reminder.reminder_label) {
      return reminder.reminder_label;
    }

    if (reminder.reminder_type === "reminder_2_months") {
      return "2 Month Reminder";
    }

    if (reminder.reminder_type === "reminder_14_months") {
      return "14 Month Reminder";
    }

    if (reminder.reminder_type === "insurance_renewal") {
      return "Insurance Renewal";
    }

    return "Reminder";
  };

  // ============================================================
  // MONEY HELPERS
  // ============================================================

  const numberValue = (value: string | number | null | undefined) => {
    const parsed = Number(value);

    if (!Number.isFinite(parsed)) {
      return 0;
    }

    return parsed;
  };

  const formatMoney = (value: number) => {
    return numberValue(value).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const getFormCost = (form: FinancialForm) => {
    return (
      numberValue(form.permit) +
      numberValue(form.insurance) +
      numberValue(form.employment_contract)
    );
  };

  const getFormTotal = (form: FinancialForm) => {
    return getFormCost(form) + numberValue(form.profit);
  };

  // ============================================================
  // FETCH DUE / OUTSTANDING REMINDERS
  // ============================================================

  const fetchDueReminders = useCallback(async () => {
    if (!token) {
      return;
    }

    try {
      setError("");

      const response = await api.get("/get-due-reminders", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.status === "success") {
        const reminders: DueReminder[] = (response.data.maids || []).map(
          (reminder: any) => ({
            ...reminder,

            completed: reminder.completed === true,

            permit: numberValue(reminder.permit),
            insurance: numberValue(reminder.insurance),
            employment_contract: numberValue(reminder.employment_contract),
            profit: numberValue(reminder.profit),
            cost: numberValue(reminder.cost),
            total: numberValue(reminder.total),
          })
        );

        setDueReminders(reminders);

        const forms: Record<string, FinancialForm> = {};

        reminders.forEach((reminder) => {
          const reminderKey = getReminderKey(reminder);

          forms[reminderKey] = {
            permit: String(reminder.permit ?? 0),
            insurance: String(reminder.insurance ?? 0),
            employment_contract: String(reminder.employment_contract ?? 0),
            profit: String(reminder.profit ?? 0),
          };
        });

        setFinancialForms(forms);
      } else {
        setDueReminders([]);
        setFinancialForms({});
        setError("No pending reminders found.");
      }
    } catch (err: any) {
      console.error("Error fetching due reminders:", err);

      setDueReminders([]);
      setFinancialForms({});

      if (err?.response?.status === 401) {
        setError(
          "Your session has expired. Please sign out and sign in again."
        );
      } else {
        setError(
          err?.response?.data?.message ||
            "An error occurred while fetching reminders."
        );
      }
    }
  }, [token]);

  // ============================================================
  // LOAD REMINDERS
  // ============================================================

  useEffect(() => {
    if (!email || !token) {
      return;
    }

    fetchDueReminders();
  }, [email, token, fetchDueReminders]);

  // ============================================================
  // EXPAND / COLLAPSE REMINDER
  // ============================================================

  const handleToggleReminder = (reminder: DueReminder) => {
    const reminderKey = getReminderKey(reminder);

    setExpandedReminder((current) =>
      current === reminderKey ? null : reminderKey
    );

    setFinancialMessages((current) => ({
      ...current,
      [reminderKey]: "",
    }));
  };

  // ============================================================
  // FINANCIAL INPUT CHANGE
  // ============================================================

  const handleFinancialChange = (
    reminderKey: string,
    field: keyof FinancialForm,
    value: string
  ) => {
    if (value !== "" && !/^\d*\.?\d{0,2}$/.test(value)) {
      return;
    }

    setFinancialForms((current) => ({
      ...current,
      [reminderKey]: {
        ...(current[reminderKey] || {
          permit: "0",
          insurance: "0",
          employment_contract: "0",
          profit: "0",
        }),
        [field]: value,
      },
    }));

    setFinancialMessages((current) => ({
      ...current,
      [reminderKey]: "",
    }));
  };

  // ============================================================
  // SAVE / UPDATE FINANCIAL VALUES
  // ============================================================

  const handleSaveFinancials = async (reminder: DueReminder) => {
    if (!token) {
      setError("Your session has expired. Please sign out and sign in again.");
      return;
    }

    const reminderKey = getReminderKey(reminder);
    const form = financialForms[reminderKey];

    if (!form) {
      return;
    }

    const permit = numberValue(form.permit);
    const insurance = numberValue(form.insurance);
    const employmentContract = numberValue(form.employment_contract);
    const profit = numberValue(form.profit);

    if (permit < 0 || insurance < 0 || employmentContract < 0 || profit < 0) {
      setFinancialMessages((current) => ({
        ...current,
        [reminderKey]: "Values cannot be negative.",
      }));

      return;
    }

    try {
      setSavingFinancials(reminderKey);

      setFinancialMessages((current) => ({
        ...current,
        [reminderKey]: "",
      }));

      const response = await api.put(
        "/reminder-financials",
        {
          maid_id: reminder.maid_id,
          reminder_type: reminder.reminder_type,
          due_date: reminder.due_date,

          permit,
          insurance,
          employment_contract: employmentContract,
          profit,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const saved = response.data;

      setDueReminders((currentReminders) =>
        currentReminders.map((item) =>
          getReminderKey(item) === reminderKey
            ? {
                ...item,
                permit: numberValue(saved.permit),
                insurance: numberValue(saved.insurance),
                employment_contract: numberValue(saved.employment_contract),
                profit: numberValue(saved.profit),
                cost: numberValue(saved.cost),
                total: numberValue(saved.total),
              }
            : item
        )
      );

      setFinancialForms((current) => ({
        ...current,
        [reminderKey]: {
          permit: String(numberValue(saved.permit)),
          insurance: String(numberValue(saved.insurance)),
          employment_contract: String(numberValue(saved.employment_contract)),
          profit: String(numberValue(saved.profit)),
        },
      }));

      setFinancialMessages((current) => ({
        ...current,
        [reminderKey]: "Financial values saved.",
      }));
    } catch (err: any) {
      console.error("Error saving reminder financials:", err);

      if (err?.response?.status === 401) {
        setError(
          "Your session has expired. Please sign out and sign in again."
        );
      } else {
        setFinancialMessages((current) => ({
          ...current,
          [reminderKey]:
            err?.response?.data?.message || "Unable to save financial values.",
        }));
      }
    } finally {
      setSavingFinancials(null);
    }
  };

  // ============================================================
  // MARK REMINDER AS DONE
  // ============================================================

  const handleDone = async (reminder: DueReminder) => {
    if (!token) {
      setError("Your session has expired. Please sign out and sign in again.");
      return;
    }

    if (reminder.completed) {
      return;
    }

    const reminderKey = getReminderKey(reminder);

    try {
      setCompletingReminder(reminderKey);
      setError("");

      await api.post(
        "/complete-reminder",
        {
          maid_id: reminder.maid_id,
          reminder_type: reminder.reminder_type,
          due_date: reminder.due_date,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setDueReminders((currentReminders) =>
        currentReminders.map((item) =>
          getReminderKey(item) === reminderKey
            ? {
                ...item,
                completed: true,
              }
            : item
        )
      );
    } catch (err: any) {
      console.error("Error completing reminder:", err);

      if (err?.response?.status === 401) {
        setError(
          "Your session has expired. Please sign out and sign in again."
        );
      } else {
        setError(
          err?.response?.data?.message || "Unable to mark the reminder as done."
        );
      }
    } finally {
      setCompletingReminder(null);
    }
  };

  // ============================================================
  // PAGE
  // ============================================================

  return (
    <div className="home-page">
      <h1>Welcome to BaytCare</h1>

      <p className={email ? "logged-in" : "not-logged-in"}>
        {email
          ? agencyName
            ? `Agency: ${agencyName}`
            : "Logged in"
          : "Not logged in"}
      </p>

      <div className="reminders">
        <h2>Due Reminders</h2>

        {error && <p className="error-message">{error}</p>}

        {dueReminders.length > 0 ? (
          <ul className="reminder-list">
            {dueReminders.map((reminder) => {
              const reminderKey = getReminderKey(reminder);

              const isCompleting = completingReminder === reminderKey;

              const isExpanded = expandedReminder === reminderKey;

              const isSaving = savingFinancials === reminderKey;

              const form = financialForms[reminderKey] || {
                permit: String(reminder.permit || 0),
                insurance: String(reminder.insurance || 0),
                employment_contract: String(reminder.employment_contract || 0),
                profit: String(reminder.profit || 0),
              };

              const liveCost = getFormCost(form);
              const liveTotal = getFormTotal(form);

              return (
                <li
                  key={reminderKey}
                  className={`reminder-card ${isExpanded ? "expanded" : ""}`}
                >
                  <div
                    className="reminder-row"
                    onClick={() => handleToggleReminder(reminder)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        handleToggleReminder(reminder);
                      }
                    }}
                  >
                    <div className="reminder-column reminder-type-column">
                      <span className="reminder-column-label">Reminder</span>

                      <span className="reminder-column-value">
                        {getReminderLabel(reminder)}
                      </span>
                    </div>

                    <div className="reminder-column reminder-client-column">
                      <span className="reminder-column-label">Client</span>

                      <span className="reminder-column-value reminder-client-value">
                        {reminder.client_name || "—"}
                      </span>
                    </div>

                    <div className="reminder-column reminder-contact-column">
                      <span className="reminder-column-label">Contact</span>

                      <span className="reminder-column-value reminder-contact-value">
                        {reminder.contact_info || "—"}
                      </span>
                    </div>

                    <div className="reminder-column">
                      <span className="reminder-column-label">Maid</span>

                      <span className="reminder-column-value">
                        {reminder.maid_name || "—"}
                      </span>
                    </div>

                    <div className="reminder-column">
                      <span className="reminder-column-label">Arrival</span>

                      <span className="reminder-column-value">
                        {reminder.arrival_date || "—"}
                      </span>
                    </div>

                    <div className="reminder-column">
                      <span className="reminder-column-label">Due Date</span>

                      <span className="reminder-column-value">
                        {reminder.due_date || "—"}
                      </span>
                    </div>

                    <div className="reminder-column reminder-total-column">
                      <span className="reminder-column-label">Total</span>

                      <span className="reminder-total-value">
                        {formatMoney(reminder.total)}
                      </span>
                    </div>

                    <div
                      className="reminder-column reminder-status-column"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <span className="reminder-column-label">Status</span>

                      <button
                        type="button"
                        className={`reminder-status-button ${
                          reminder.completed ? "done" : "pending"
                        }`}
                        onClick={() => handleDone(reminder)}
                        disabled={reminder.completed || isCompleting}
                      >
                        {isCompleting
                          ? "Saving..."
                          : reminder.completed
                          ? "Done"
                          : "Pending"}
                      </button>
                    </div>

                    <div className="reminder-expand-column">
                      <span
                        className={`reminder-chevron ${
                          isExpanded ? "open" : ""
                        }`}
                        aria-hidden="true"
                      >
                        ▼
                      </span>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="reminder-financial-panel">
                      <div className="financial-panel-header">
                        <div>
                          <h3>Reminder Financials</h3>

                          <p>
                            Enter the costs and agency profit for this reminder.
                          </p>
                        </div>
                      </div>

                      <div className="financial-input-grid">
                        <label className="financial-field">
                          <span>Permit</span>

                          <input
                            type="text"
                            inputMode="decimal"
                            value={form.permit}
                            onChange={(event) =>
                              handleFinancialChange(
                                reminderKey,
                                "permit",
                                event.target.value
                              )
                            }
                            onClick={(event) => event.stopPropagation()}
                          />
                        </label>

                        <label className="financial-field">
                          <span>Insurance</span>

                          <input
                            type="text"
                            inputMode="decimal"
                            value={form.insurance}
                            onChange={(event) =>
                              handleFinancialChange(
                                reminderKey,
                                "insurance",
                                event.target.value
                              )
                            }
                            onClick={(event) => event.stopPropagation()}
                          />
                        </label>

                        <label className="financial-field">
                          <span>Employment Contract</span>

                          <input
                            type="text"
                            inputMode="decimal"
                            value={form.employment_contract}
                            onChange={(event) =>
                              handleFinancialChange(
                                reminderKey,
                                "employment_contract",
                                event.target.value
                              )
                            }
                            onClick={(event) => event.stopPropagation()}
                          />
                        </label>

                        <label className="financial-field">
                          <span>Profit</span>

                          <input
                            type="text"
                            inputMode="decimal"
                            value={form.profit}
                            onChange={(event) =>
                              handleFinancialChange(
                                reminderKey,
                                "profit",
                                event.target.value
                              )
                            }
                            onClick={(event) => event.stopPropagation()}
                          />
                        </label>
                      </div>

                      <div className="financial-summary">
                        <div className="financial-summary-item">
                          <span>Direct Cost</span>

                          <strong>{formatMoney(liveCost)}</strong>
                        </div>

                        <div className="financial-summary-item total">
                          <span>Total</span>

                          <strong>{formatMoney(liveTotal)}</strong>
                        </div>
                      </div>

                      <div className="financial-actions">
                        {financialMessages[reminderKey] && (
                          <span className="financial-message">
                            {financialMessages[reminderKey]}
                          </span>
                        )}

                        <button
                          type="button"
                          className="save-financials-button"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleSaveFinancials(reminder);
                          }}
                          disabled={isSaving}
                        >
                          {isSaving ? "Saving..." : "Save Changes"}
                        </button>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        ) : (
          !error && <p className="no-reminders">No pending reminders.</p>
        )}
      </div>
    </div>
  );
};

export default HomePage;
